
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Accordion } from "@/components/ui/accordion";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SelectChildDialog } from "@/components/test/SelectChildDialog";
import { AgeLevelSection } from "@/components/testing/AgeLevelSection";
import { getAllCategories } from "@/utils/categoryMapping";
import { Card, CardContent } from "@/components/ui/card";

const AGE_LEVELS = [
  "Infant",
  "Age 1",
  "Age 2",
  "Age 3",
  "Age 4",
  "Age 5",
  "Age 6"
];

// Use the centralized category mapping
const CATEGORIES = getAllCategories().map(name => ({ name }));

const TestingModules = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [selectedTest, setSelectedTest] = useState<{ grade: string; category: string; level: string } | null>(null);
  const [showChildDialog, setShowChildDialog] = useState(false);

  const { data: tests, isLoading } = useQuery({
    queryKey: ["tests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tests")
        .select("*")
        .order('difficulty');
      
      if (error) {
        console.error("Error fetching tests:", error);
        throw error;
      }
      return data;
    }
  });

  const handleLevelClick = async (age: string, category: string, level: string) => {
    setSelectedTest({ grade: age, category, level });
    setShowChildDialog(true);
  };

  const handleChildSelect = async (childId: string) => {
    if (!selectedTest) return;

    try {
      const { data, error } = await supabase
        .from("tests")
        .select("id")
        .eq("grade_level", selectedTest.grade)
        .eq("category", selectedTest.category)
        .eq("title", selectedTest.level)
        .limit(1);

      if (error) throw error;
      if (!data || data.length === 0) {
        toast({
          title: "Test not available",
          description: `No test found for ${selectedTest.category} ${selectedTest.level} in ${selectedTest.grade}`,
          variant: "destructive"
        });
        return;
      }

      localStorage.setItem("selectedChildId", childId);
      navigate(`/test/${data[0].id}`);
    } catch (error: any) {
      console.error("Error finding test:", error);
      toast({
        title: "Error",
        description: "Failed to load the test. Please try again.",
        variant: "destructive"
      });
    } finally {
      setShowChildDialog(false);
      setSelectedTest(null);
    }
  };

  const handleCategoryClick = (categoryId: string) => {
    setOpenCategory(openCategory === categoryId ? null : categoryId);
  };

  const getLevelsForAgeAndCategory = (age: string, category: string) => {
    if (!tests) return [];
    
    const ageTests = tests.filter(
      test => test.grade_level === age && test.category === category
    );

    return ageTests.map(test => ({
      name: test.title,
      difficulty: test.difficulty,
      title: test.title,
      id: test.id // Include the test ID
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center">
            Loading tests...
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          
          <h1 className="text-3xl font-heading font-bold text-gray-900 mb-6">
            Testing Modules
          </h1>

          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <Info className="w-6 h-6 text-blue-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-600">
                    Explore our assessment modules below to track your child's growth. For a personalized view of completed tests and results, access the modules directly from your child's profile.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Accordion type="single" collapsible className="space-y-4">
            {AGE_LEVELS.map((age) => (
              <AgeLevelSection
                key={age}
                age={age}
                categories={CATEGORIES}
                openCategory={openCategory}
                onCategoryClick={handleCategoryClick}
                getLevelsForAgeAndCategory={getLevelsForAgeAndCategory}
                onLevelClick={handleLevelClick}
              />
            ))}
          </Accordion>
        </div>

        <SelectChildDialog
          open={showChildDialog}
          onClose={() => {
            setShowChildDialog(false);
            setSelectedTest(null);
          }}
          onSelect={handleChildSelect}
        />
      </main>
    </div>
  );
};

export default TestingModules;
